// src/App.js
//
// ⚠️ Performance: every route component below is loaded via React.lazy() so
// the initial JS bundle only contains the AppShell + the small set of
// always-needed pieces (TopMenuBar, AuthProvider, NotFound, etc). The huge
// route components (SuperAdminPage ~16k LOC, AppLive ~6k, ChatApp ~7k,
// DatingTemplate ~9k) are split into their own chunks and only fetched
// when the user actually navigates there. UI/UX is unchanged — Suspense
// fallback is `null`, so a brief chunk load just looks like the previous
// "page transitioning" state, identical to before.
import React, {
  Suspense,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { captureReferralFromUrl } from "./growth/referral";

import "./App.css";

// ─── Eagerly imported (always present in the AppShell) ─────────────────
import { AuthProvider } from "./user/AuthContext";
import PrivateRoute from "./user/PrivateRoute";
import NotFound from "./components/NotFound";
// Crash net + route-loading shell. These intentionally have ZERO
// dependencies on lazy chunks so they're always available even when
// the rest of the app is mid-deploy / mid-fetch. See the file headers
// for the white-screen scenarios they each cover.
import ErrorBoundary from "./components/ErrorBoundary";
import RouteFallback from "./components/RouteFallback";
import PromoPill from "./promo/PromoPill";

// Slug resolver is only needed on /:slug* routes. Keep it out of the
// startup chunk so "/" -> "/build" first paint stays lean.
let appmakerResolverModulePromise = null;
function loadAppmakerResolverModule() {
  if (!appmakerResolverModulePromise) {
    appmakerResolverModulePromise = import("./firebase-modular/firebase-appmaker");
  }
  return appmakerResolverModulePromise;
}

// ─── Route components — lazy loaded on first navigation ────────────────
const TopMenuBar = React.lazy(() => import("./components/TopMenuBar"));
const ForYou = React.lazy(() => import("./pages/ForYou"));
const Partners = React.lazy(() => import("./components/partners"));
// LEX Studio — internal "Create Any App" surface, password-gated (route /studio)
const MyAppsStudio = React.lazy(() => import("./pages/MyAppsStudio"));
// Verified internal tool — Content Multiplier (route /studio/tools/content-multiplier)
const ContentMultiplier = React.lazy(() => import("./pages/ContentMultiplier"));
// Verified internal tool — Content Engine + Calendar (route /studio/tools/content-engine)
const ContentEngine = React.lazy(() => import("./pages/ContentEngine"));
// Verified internal tool — Mailboxes / WHM email manager (route /studio/tools/mailboxes)
const StudioMailboxes = React.lazy(() => import("./pages/StudioMailboxes"));
// Verified internal tool — Social overview (IG/Twitter stats) (route /studio/tools/social)
const StudioSocial = React.lazy(() => import("./pages/StudioSocial"));
// Verified internal tool — Instagram automation (8-account command center) (route /studio/tools/ig)
const StudioIG = React.lazy(() => import("./pages/StudioIG"));
// Verified internal tool — Social media automation suite (plan→localize→publish→measure) (route /studio/tools/suite)
const MarketingSuite = React.lazy(() => import("./pages/MarketingSuite"));
// Verified internal tool — Instantly lead prep (raw seeds → CSV/XLSX) (route /studio/tools/instantly)
const StudioInstantly = React.lazy(() => import("./pages/StudioInstantly"));

// Login / Sign-up v2 — phone-only OTP over a mosaic+blue-gradient
// surface. v1 (./user/Login, ./user/SignUp) is retained on disk as a
// SuperAdmin product-skill archive (see docs/skills/superadmin/product/
// auth_v1_phone_email_social.md) but is no longer mounted as a route.
const SignUp = React.lazy(() => import("./user/SignUpV2"));
const Login = React.lazy(() => import("./user/LoginV2"));
// Rendered once at the App level (not inside Login/SignUp) so it stays
// mounted while toggling /login ↔ /signup — remounting it would restart
// the scroll animation and make the background visibly jump.
const LiveCreateMosaicBackground = React.lazy(() =>
  import("./appbuilder/LiveCreateMosaicBackground")
);
const ForgotPassword = React.lazy(() => import("./user/ForgotPassword"));
const UserDashboard = React.lazy(() => import("./user/UserDashboard"));
const ProfilePanel = React.lazy(() => import("./user/ProfilePanel"));
const BillingPanel = React.lazy(() => import("./user/BillingPanel"));
const CoinsGiftPanel = React.lazy(() => import("./user/CoinsGiftPanel"));
const ConnectedChannels = React.lazy(() => import("./user/ConnectedChannels"));
const Integrations = React.lazy(() => import("./pages/Integrations"));
const Bind = React.lazy(() => import("./pages/Bind"));

// 2026-05-08: /api/* hub. Layout owns its own left sub-nav (modeled on
// platform.openai.com); each child route renders inside its <Outlet/>.
// /api/connections re-uses <ConnectedChannels/> so the new hub and the
// existing /profile/channels share a single source of truth.
const ApiLayout = React.lazy(() => import("./api/ApiLayout"));
const ApiOverview = React.lazy(() => import("./api/ApiOverview"));
const ApiKeys = React.lazy(() => import("./api/ApiKeys"));
const ApiConnections = React.lazy(() => import("./api/ApiConnections"));
const ApiWebhooks = React.lazy(() => import("./api/ApiWebhooks"));
const ApiUsage = React.lazy(() => import("./api/ApiUsage"));

const LexMail = React.lazy(() => import("./Lexmail/LexMail"));

const NewTeamMember = React.lazy(() => import("./pages/NewTeamMember"));

const PrivacyPolicy = React.lazy(() => import("./components/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./components/TermsOfService"));

const OptInPage = React.lazy(() => import("./components/OptInPage"));
const Lexburner = React.lazy(() => import("./Lexburner/Lexburner"));

const Media = React.lazy(() => import("./media/media"));
const AppStore = React.lazy(() => import("./Lexappstore/AppStore"));
const AppDetail = React.lazy(() => import("./Lexappstore/appdetail"));
const BurnerChat = React.lazy(() => import("./miniapps/Lexburnerchat/BurnerChat"));

const StripeCancel = React.lazy(() => import("./Lexappstore/payment-stripe-cancel"));
const StripeSuccess = React.lazy(() => import("./Lexappstore/payment-stripe-success"));
const LexEmailAutomation = React.lazy(() => import("./miniapps/lex-emailautomation/LexEmailAutomation"));
const LexOpenCalender = React.lazy(() => import("./miniapps/lex-open-calender/lex-open-calender"));
const LexWhenToMeet = React.lazy(() => import("./miniapps/lex-whentomeet/lex-whentomeet"));
const LexMessage = React.lazy(() => import("./miniapps/lex-message/lex-message"));
const AppBuilder = React.lazy(() => import("./appbuilder/appbuilder"));
const PricingPage = React.lazy(() => import("./appbuilder/PricingPage"));
const MyAppRail = React.lazy(() => import("./appbuilder/myapp"));
const MyAppsAdvanced = React.lazy(() => import("./appbuilder/MyAppsAdvanced"));
const SuperAdminPage = React.lazy(() => import("./appbuilder/SuperAdminPage"));
const AppLive = React.lazy(() => import("./appbuilder/AppLive.jsx"));
const ListingPage = React.lazy(() => import("./appbuilder/templates/ListingPage"));
const ShortLinkResolver = React.lazy(() => import("./growth/ShortLinkResolver"));

const DemoSportsCards = React.lazy(() => import("./demo/sportscards"));
const SportsCardsDashboard = React.lazy(() => import("./demo/dashboard"));
const CardsHome = React.lazy(() => import("./demo/cardshome"));

const ManageListing = React.lazy(() => import("./appbuilder/templates/ManageListing"));

const LexLiveTranslate = React.lazy(() => import("./miniapps/lex-live-translate/lex-live-translate"));
const ChatApp = React.lazy(() => import("./appbuilder/chat/chat"));

// App Store "Add-ons" — dating mini-apps
const ResumeDating = React.lazy(() => import("./miniapps/lex-dating-addons/ResumeDating"));
const ZodiacDating = React.lazy(() => import("./miniapps/lex-dating-addons/ZodiacDating"));
const BaziMatch = React.lazy(() => import("./miniapps/lex-dating-addons/BaziMatch"));

const AUTH_RETURN_SCROLL_KEY = "lex:auth-return-scroll";

// Popup helper
const PopupListener = ({ setIsPopupOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.openPopup) {
      setIsPopupOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    } else if (/^\/p\/[^/]+$/.test(location.pathname)) {
      setIsPopupOpen(true);
    }
  }, [location, setIsPopupOpen, navigate]);

  return null;
};

/* ──────────────────────────────────────────────
   ✅ SlugLanding: 渲染 AppLive (通过 Firebase 确认是否为 app)
   ────────────────────────────────────────────── */
function SlugLanding() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { resolveBySlug } = await loadAppmakerResolverModule();
        const target = await resolveBySlug(slug);
        if (!alive) return;
        if (!target) {
          setState({ loading: false, error: "NOT_FOUND", data: null });
          return;
        }
        setState({ loading: false, error: null, data: target });
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, error: e?.message || "ERROR", data: null });
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  if (state.loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (state.error || !state.data) return <NotFound />;

  const { uid, appId } = state.data;
  return <AppLive publicOverride={{ ownerUid: uid, appId }} />;
}

/* ──────────────────────────────────────────────
   ✅ ProfileSlugLanding: /:appSlug/:personSlug
   解析 (appSlug, personSlug) → (uid, appId, listingId)
   渲染 AppLive，并把 profileId 通过 publicOverride 透传
   ────────────────────────────────────────────── */
function ProfileSlugLanding() {
  const { appSlug, personSlug } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { resolveListingBySlug } = await loadAppmakerResolverModule();
        const target = await resolveListingBySlug(appSlug, personSlug);
        if (!alive) return;
        if (!target) {
          setState({ loading: false, error: "NOT_FOUND", data: null });
          return;
        }
        setState({ loading: false, error: null, data: target });
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, error: e?.message || "ERROR", data: null });
      }
    })();
    return () => {
      alive = false;
    };
  }, [appSlug, personSlug]);

  if (state.loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (state.error || !state.data) return <NotFound />;

  const { uid, appId, listingId } = state.data;
  return (
    <AppLive
      publicOverride={{ ownerUid: uid, appId, profileId: listingId }}
    />
  );
}

/* ──────────────────────────────────────────────
   APP SHELL — 控制 TopNav 是否显示（通过 Firebase 检测）
   + Modal Route 背景/覆盖 路由结构
   ────────────────────────────────────────────── */
function AppShell() {
  const [, setIsPopupOpen] = useState(false);
  const [gestureEnabled, setGestureEnabled] = useState(
    () => localStorage.getItem("gestureEnabled") === "true"
  );
  const [isFirebaseApp, setIsFirebaseApp] = useState(false);
  const [isChatWindowOpen, setIsChatWindowOpen] = useState(false);
  const [isGodModeOpen, setIsGodModeOpen] = useState(false);
  const [isJoinPaywallOpen, setIsJoinPaywallOpen] = useState(false);
  const [topNavHeight, setTopNavHeight] = useState(0);
  // Once the user enters /app/new/live, treat any subsequent /app/{id}/live
  // route as a continuation of the same create flow and keep the top nav
  // hidden. Without this flag the nav would re-appear the moment the
  // publishNow handler navigates from /app/new/live → /app/{appId}/live,
  // breaking the immersive create experience the design wants. Cleared
  // when the user navigates anywhere outside the /app/... routes.
  const [appCreateSessionActive, setAppCreateSessionActive] = useState(false);
  const topNavHostRef = useRef(null);
  const contentScrollRef = useRef(null);

  const location = useLocation();
  const state = location.state || null; // ← Modal Route 关键：保留 returnPath/owner overrides
  const query = new URLSearchParams(location.search);
  const prevLocationForScrollResetRef = useRef({
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    try {
      window.sessionStorage.removeItem(AUTH_RETURN_SCROLL_KEY);
    } catch {}
  }, [location.pathname, location.search, location.hash, location.key]);

  // ── Referral attribution: on any page load, read ?ref & ?c, stash first-touch
  // to localStorage (30d), then strip the params so they aren't re-shared.
  // The persist-on-join step reads this back. See src/growth/referral.js.
  useEffect(() => {
    try {
      captureReferralFromUrl();
    } catch {}
  }, [location.pathname, location.search]);

  // ── Once at app boot: kill the browser's auto scroll-restoration. Without
  // this, Chrome/Safari/Firefox try to restore the previous scroll Y on back/
  // forward and some SPA push transitions, racing our reset and winning.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
    } catch {}
  }, []);

  // Force reset scroll to top on EVERY navigation.
  // Strategy: ONLY touch the six well-known scrollers (window, html, body,
  // scrollingElement, #root, main content scroller, [data-mobile-scroller]).
  // No querySelectorAll, no getComputedStyle, no DOM walk — those operations
  // froze the main thread on mobile when pages had thousands of elements.
  // 99.9% of routes scroll one of these six, so this is sufficient.
  //
  // EXCEPTION: skip the reset when the user is mid-create-flow and we just
  // post-publish-navigated from /app/new/live → /app/{appId}/live. The
  // pathname changed but the user is still on the same logical page (the
  // last card of the create flow); snapping scroll{Top,Left} to 0 here
  // would yank them back to the first card horizontally and to the top
  // vertically, which contradicts the spec ("stay on the last card after
  // publish"). The pathname check is duplicated inline here because
  // isAppCreateContinuation is declared below this effect — we can't
  // reference it from this effect's closure or deps without a TDZ error.
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const prevLoc = prevLocationForScrollResetRef.current || {
      pathname: "",
      search: "",
      hash: "",
    };
    let skipResetForAdvancedAppSwitch = false;
    if (
      location.pathname === "/my-apps/advanced" &&
      prevLoc.pathname === location.pathname &&
      prevLoc.hash === location.hash
    ) {
      const prevParams = new URLSearchParams(prevLoc.search || "");
      const nextParams = new URLSearchParams(location.search || "");
      const prevAppId = String(prevParams.get("appId") || "").trim();
      const nextAppId = String(nextParams.get("appId") || "").trim();
      prevParams.delete("appId");
      nextParams.delete("appId");
      skipResetForAdvancedAppSwitch =
        prevAppId !== nextAppId && prevParams.toString() === nextParams.toString();
    }
    prevLocationForScrollResetRef.current = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };

    const isCreateFlowContinuation =
      appCreateSessionActive &&
      (/^\/app\/[^/]+\/live(?:\/.*)?$/.test(location.pathname) ||
        /^\/app\/[^/]+\/[^/]+\/live(?:\/.*)?$/.test(location.pathname));
    if (isCreateFlowContinuation || skipResetForAdvancedAppSwitch) return;

    const resetNode = (node) => {
      if (!node) return;
      try { node.scrollTop = 0; node.scrollLeft = 0; } catch {}
    };
    const resetAll = () => {
      try { window.scrollTo(0, 0); } catch {}
      resetNode(document.documentElement);
      resetNode(document.body);
      resetNode(document.scrollingElement);
      // ⚠️ #root can implicitly become the scroller (CSS `overflow-x: hidden`
      // + `min-height: 100vh` → browser computes overflow-y as auto). It's
      // never unmounted, so its scrollTop sticks across routes if not reset.
      resetNode(document.getElementById("root"));
      resetNode(contentScrollRef.current);
      resetNode(document.querySelector("[data-mobile-scroller]"));
    };

    // Pass 1 — sync, before paint commits. Constant time, sub-millisecond.
    resetAll();
    // Pass 2 — next frame, in case async content shifted layout. Still cheap.
    const rafId = window.requestAnimationFrame(resetAll);

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [
    location.pathname,
    location.search,
    location.hash,
    location.key,
    appCreateSessionActive,
  ]);

  const toggleGesture = useCallback(() => setGestureEnabled((e) => !e), []);
  const handleOpenPopup = useCallback(() => setIsPopupOpen(true), []);

  useEffect(() => {
    const onChatWindowVisible = (e) => {
      const open = Boolean(e?.detail?.open);
      setIsChatWindowOpen(open);
    };
    window.addEventListener("lex:chat-window-visible", onChatWindowVisible);
    return () => window.removeEventListener("lex:chat-window-visible", onChatWindowVisible);
  }, []);

  useEffect(() => {
    const onGodModeVisible = (e) => {
      const open = Boolean(e?.detail?.open);
      setIsGodModeOpen(open);
    };
    window.addEventListener("lex:godmode-visible", onGodModeVisible);
    return () => window.removeEventListener("lex:godmode-visible", onGodModeVisible);
  }, []);

  useEffect(() => {
    const onJoinPaywallVisible = (e) => {
      const open = Boolean(e?.detail?.open);
      setIsJoinPaywallOpen(open);
    };
    window.addEventListener("lex:join-paywall-visible", onJoinPaywallVisible);
    return () => window.removeEventListener("lex:join-paywall-visible", onJoinPaywallVisible);
  }, []);

  useEffect(() => {
    const swallowResizeObserverNoise = (event) => {
      const msg = String(event?.message || "");
      if (
        msg.includes("ResizeObserver loop completed with undelivered notifications") ||
        msg.includes("ResizeObserver loop limit exceeded")
      ) {
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
      }
    };

    const swallowResizeObserverRejection = (event) => {
      const reason = event?.reason;
      const msg = String(reason?.message || reason || "");
      if (
        msg.includes("ResizeObserver loop completed with undelivered notifications") ||
        msg.includes("ResizeObserver loop limit exceeded")
      ) {
        event.preventDefault?.();
      }
    };

    window.addEventListener("error", swallowResizeObserverNoise, true);
    window.addEventListener("unhandledrejection", swallowResizeObserverRejection, true);
    return () => {
      window.removeEventListener("error", swallowResizeObserverNoise, true);
      window.removeEventListener("unhandledrejection", swallowResizeObserverRejection, true);
    };
  }, []);

  // 🔍 检测是否是公开 App：Firebase 级别验证（使用首段路径：/:slug/*）
  useEffect(() => {
    const checkSlug = async () => {
      const first = location.pathname.split("/").filter(Boolean)[0];
      if (!first) {
        setIsFirebaseApp(false);
        return;
      }

      // 这些是保留路由名，不能当作 slug 查询
      const RESERVED = new Set([
        // 顶层常规页
        "build",
        "pricing",
        "preview",
        "apps",
        "app",
        "signup",
        "login",
        "forgot-password",
        "profile",
        "admin",
        "mail",
        "newteammember",
        "studio",
        "our-company",
        "startups",
        "people",

        // 合作伙伴 & 媒体
        "partners",
        "lexpartners",
        "newpartner",
        "media",
        "appstore",
        "lexappstore",

        // miniapps & 工具
        "burnerchat",
        "email-automation",
        "opencalendar",
        "whentomeet",
        "message",

        // 支付回调
        "cancel",
        "success",

        // 合规
        "privacy",
        "terms",

        "appbuilder",
      ]);

      if (RESERVED.has(first)) {
        setIsFirebaseApp(false);
        return;
      }

      try {
        const { resolveBySlug } = await loadAppmakerResolverModule();
        const data = await resolveBySlug(first);
        setIsFirebaseApp(!!data);
      } catch {
        setIsFirebaseApp(false);
      }
    };
    checkSlug();
  }, [location.pathname]);

  // ✅ hide nav only if: /app/:id/live?pub=1 (包括子路径) 或 Firebase slug 有效
  // ✅ UPDATED: support BOTH legacy (/app/:appId/live) AND new (/app/:ownerUid/:appId/live)
  const isPublicLive =
    (/^\/app\/[^/]+\/live(?:\/.*)?$/.test(location.pathname) || // legacy /app/:appId/live
      /^\/app\/[^/]+\/[^/]+\/live(?:\/.*)?$/.test(location.pathname)) && // new /app/:ownerUid/:appId/live
    query.get("pub") === "1";

  const isChatRoute = /^\/appbuilder\/chat(?:\/[^/]+\/[^/]+)?$/.test(location.pathname);
  const isNoTopNavMiniAppRoute =
    /^\/burnerchat(?:\/.*)?$/.test(location.pathname) ||
    /^\/call(?:\/.*)?$/.test(location.pathname);
  const isSparkNearbySetupRoute =
    location.pathname === "/app/new/live" &&
    (String(query.get("template") || "").toLowerCase() === "nearby" ||
      String(query.get("addon") || "").toLowerCase() === "spark-nearby");
  // Hide the top nav (Home / App Store / etc.) on the app create page so
  // the create flow takes the full viewport without the marketing chrome.
  const isAppCreateRoute = location.pathname === "/app/new/live";
  // Continuation of the create flow after publish: pathname has been
  // replaced from /app/new/live → /app/{appId}/live but we still want
  // the immersive layout (no top nav). appCreateSessionActive is set
  // when the user enters /app/new/live and stays true while they're on
  // any /app/.../live route. Still consumed by the scroll-reset effects
  // below to skip snapping to the top during the post-publish navigate.
  const isAppCreateContinuation =
    appCreateSessionActive &&
    (/^\/app\/[^/]+\/live(?:\/.*)?$/.test(location.pathname) ||
      /^\/app\/[^/]+\/[^/]+\/live(?:\/.*)?$/.test(location.pathname));
  // Any owner-mode /app/.../live route — covers all the entry points
  // the user thinks of as "the create page": legacy /app/new/live, the
  // "+ New App" button (which now creates an empty app and lands on
  // /app/{newId}/live), and the "I Built" edit entry on /app/{id}/live.
  // The user's spec is "these should all be consistent" — same mosaic
  // background, same hidden top nav. Public viewers (?pub=1 or
  // SlugLanding publicOverride) are excluded via the isPublicLive
  // check above, so they keep the normal page chrome.
  const isOwnerLiveRoute =
    !isPublicLive &&
    (/^\/app\/[^/]+\/live(?:\/.*)?$/.test(location.pathname) ||
      /^\/app\/[^/]+\/[^/]+\/live(?:\/.*)?$/.test(location.pathname));

  // Track create-flow session: turn it on when the user enters
  // /app/new/live, leave it on while they're on any /app/.../live route
  // (including the post-publish /app/{appId}/live), and clear it the
  // moment they navigate to anything outside the builder. This is the
  // companion to the AppLive-internal didStartFromCreateFlow flag that
  // keeps the mosaic background; together they make the create page
  // visually continuous before and after publish.
  useEffect(() => {
    if (location.pathname === "/app/new/live") {
      setAppCreateSessionActive(true);
      return;
    }
    if (!/^\/app\//.test(location.pathname)) {
      setAppCreateSessionActive(false);
    }
  }, [location.pathname]);
  const isListingRoute =
    /^\/app\/[^/]+\/[^/]+\/live\/listing(?:-manage)?(?:\/.*)?$/.test(location.pathname) ||
    /^\/app\/[^/]+\/live\/listing(?:-manage)?(?:\/.*)?$/.test(location.pathname) ||
    /^\/apps\/[^/]+\/listing\/[^/]+(?:\/.*)?$/.test(location.pathname) ||
    /^\/[^/]+\/listing(?:-manage)?(?:\/.*)?$/.test(location.pathname) ||
    /^\/listing(?:-manage)?(?:\/.*)?$/.test(location.pathname);

  // Auth v2 pages own the full viewport — the mosaic background and the
  // blue-gradient panel are meant to be edge-to-edge. Match /login,
  // /signup, and the scoped /app/:ownerUid/:appId/login.
  const isAuthV2Route =
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    /^\/app\/[^/]+\/[^/]+\/login\/?$/.test(location.pathname);

  const shouldHideTopNav =
    isPublicLive ||
    isFirebaseApp ||
    isChatRoute ||
    isNoTopNavMiniAppRoute ||
    isSparkNearbySetupRoute ||
    isAppCreateRoute ||
    isAppCreateContinuation ||
    isOwnerLiveRoute ||
    isChatWindowOpen ||
    isGodModeOpen ||
    isJoinPaywallOpen ||
    isListingRoute ||
    isAuthV2Route;
  const isBuilderRoute =
    location.pathname === "/build" ||
    /^\/app\/[^/]+\/live(?:\/.*)?$/.test(location.pathname) ||
    /^\/app\/[^/]+\/[^/]+\/live(?:\/.*)?$/.test(location.pathname);

  useEffect(() => {
    if (shouldHideTopNav) {
      setTopNavHeight((prev) => (prev === 0 ? prev : 0));
      return;
    }

    const host = topNavHostRef.current;
    const target = host?.querySelector?.(".menuBar") || host;
    if (!target) {
      setTopNavHeight((prev) => (prev === 0 ? prev : 0));
      return;
    }

    let rafId = 0;
    const measureNow = () => {
      const h = Math.ceil(target.getBoundingClientRect().height || target.offsetHeight || 0);
      setTopNavHeight((prev) => (prev === h ? prev : h));
      rafId = 0;
    };

    const scheduleMeasure = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(measureNow);
    };

    scheduleMeasure();
    window.addEventListener("resize", scheduleMeasure, { passive: true });

    let mo = null;
    if (typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(() => {
        scheduleMeasure();
      });
      mo.observe(target, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const t1 = window.setTimeout(scheduleMeasure, 120);
    const t2 = window.setTimeout(scheduleMeasure, 320);
    if (document?.fonts?.ready?.then) {
      document.fonts.ready.then(scheduleMeasure).catch(() => {});
    }

    return () => {
      window.removeEventListener("resize", scheduleMeasure);
      if (mo) mo.disconnect();
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [shouldHideTopNav]);

  useEffect(() => {
    if (!isBuilderRoute) return;
    // Same exception as the global scroll-reset above: when the user
    // post-publish-navigates inside the create flow we want them on the
    // exact card they were on (the last one), not snapped back to the
    // first card. isAppCreateContinuation is in scope here because this
    // effect lives below the const declaration, unlike the layoutEffect
    // earlier in the body.
    if (isAppCreateContinuation) return;
    const scroller = contentScrollRef.current;
    if (!scroller) return;

    const snapTop = () => {
      scroller.scrollTop = 0;
      scroller.scrollLeft = 0;
    };

    snapTop();
    const rafId = window.requestAnimationFrame(snapTop);
    const timer = window.setTimeout(snapTop, 120);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timer);
    };
  }, [
    isBuilderRoute,
    location.pathname,
    location.search,
    isAppCreateContinuation,
  ]);

  const topNavOffset = shouldHideTopNav ? 0 : Math.max(topNavHeight, 44);
  const builderTopSafeGap = isBuilderRoute ? 12 : 0;

  return (
    <>
      {isAuthV2Route && (
        <Suspense fallback={null}>
          <LiveCreateMosaicBackground enabled />
        </Suspense>
      )}
      {!shouldHideTopNav && (
        <div ref={topNavHostRef}>
          <Suspense fallback={<div style={{ minHeight: 44 }} />}>
            <TopMenuBar
              onOpenPopup={handleOpenPopup}
              gestureEnabled={gestureEnabled}
              onToggleGesture={toggleGesture}
            />
          </Suspense>
        </div>
      )}
      <PopupListener setIsPopupOpen={setIsPopupOpen} />

      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          // Expose the measured top-nav height as a CSS variable so any
          // viewport-fixed UI (e.g. mobile drawers in MyAppsAdvanced) can
          // start flush against the nav's bottom edge instead of being
          // covered by it. Updates automatically when the nav resizes.
          "--lex-topnav-offset": `${topNavOffset}px`,
        }}
      >
        <div style={{ display: "flex", flex: 1 }}>
          <div
            ref={contentScrollRef}
            style={{
              flex: 1,
              paddingTop: topNavOffset + builderTopSafeGap,
              overflowY: "auto",
              transition: "margin 0.3s ease",
            }}
          >
            {/* 背景路由：正常渲染所有页面（含 listing），当带 background 状态时，这里显示“背景页”
                ⚠️ Previously used `fallback={null}`, which produced a fully
                white viewport whenever a lazy chunk was still in flight
                (slow mobile network, or cached HTML pointing at a chunk
                hash that no longer exists post-deploy). We now render a
                tiny spinner via <RouteFallback /> so users always see
                *something*, and an <ErrorBoundary> wraps the whole
                Routes block so any thrown error inside a lazy route
                shows a recoverable card instead of blanking the tree.
                The boundary also auto-reloads ChunkLoadError once with
                a cache buster — that single fix kills the most common
                "white screen after deploy" report. */}
            <ErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
            <Routes location={state?.background || location}>
              <Route path="/" element={<Navigate to="/comedian" replace />} />

              <Route path="/appbuilder/chat" element={<ChatApp />} />
              <Route path="/appbuilder/chat/:ownerUid/:appId" element={<ChatApp />} />

              <Route path="/translate-live" element={<LexLiveTranslate />} />

              {/* App Store add-ons — dating mini-apps */}
              <Route path="/resume-dating" element={<ResumeDating />} />
              <Route path="/zodiac-dating" element={<ZodiacDating />} />
              <Route path="/bazi-match" element={<BaziMatch />} />

              <Route path="/startups" element={<ForYou />} />
              <Route path="/studio" element={<MyAppsStudio />} />
              <Route path="/studio/tools/content-multiplier" element={<ContentMultiplier />} />
              <Route path="/studio/tools/content-engine" element={<ContentEngine />} />
              <Route path="/studio/tools/mailboxes" element={<StudioMailboxes />} />
              <Route path="/studio/tools/social" element={<StudioSocial />} />
              <Route path="/studio/tools/ig" element={<StudioIG />} />
              <Route path="/studio/tools/suite" element={<MarketingSuite />} />
              <Route path="/studio/tools/instantly" element={<StudioInstantly />} />
              <Route path="/lexpartners" element={<Partners />} />

              <Route path="/signup" element={<SignUp />} />

              {/* ✅ NEW scoped login route */}
              <Route path="/app/:ownerUid/:appId/login" element={<Login />} />

              {/* legacy login */}
              <Route path="/login" element={<Login />} />

              <Route path="/forgot-password" element={<ForgotPassword />} />

              <Route
                path="/profile/*"
                element={
                  <PrivateRoute>
                    <UserDashboard />
                  </PrivateRoute>
                }
              >
                <Route index element={<ProfilePanel />} />
                <Route path="account" element={<ProfilePanel />} />
                <Route path="security" element={<Navigate to="/profile" replace />} />
                <Route path="wallet" element={<BillingPanel />} />
                <Route path="billing" element={<BillingPanel />} />
                <Route path="coins" element={<CoinsGiftPanel />} />
                <Route path="channels" element={<ConnectedChannels />} />
              </Route>

              <Route path="/integrations" element={<Integrations />} />
              <Route path="/bind" element={<Bind />} />

              {/* 2026-05-08: API hub. PrivateRoute wraps the whole shell;
                  ApiLayout supplies the left sub-nav and an <Outlet/>
                  that renders the active child page. */}
              <Route
                path="/api/*"
                element={
                  <PrivateRoute>
                    <ApiLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<ApiOverview />} />
                <Route path="overview"    element={<ApiOverview />} />
                <Route path="keys"        element={<ApiKeys />} />
                <Route path="connections" element={<ApiConnections />} />
                <Route path="webhooks"    element={<ApiWebhooks />} />
                <Route path="usage"       element={<ApiUsage />} />
              </Route>

              <Route path="/our-company" element={<Navigate to="/about-us" replace />} />

              <Route path="/mail" element={<LexMail />} />
              <Route path="/newteammember" element={<NewTeamMember />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/opt-in" element={<OptInPage />} />
              <Route path="/call" element={<Lexburner />} />

              <Route path="/partners" element={<Navigate to="/our-partnerships" replace />} />
              <Route path="/partners/:partnerId" element={<Navigate to="/our-partnerships" replace />} />
              <Route path="/partners/apply" element={<Navigate to="/our-partnerships" replace />} />
              <Route path="/newpartner" element={<Navigate to="/our-partnerships" replace />} />
              <Route path="/media" element={<Media />} />
              <Route path="/app/*" element={<AppStore />} />
              <Route path="/lexappstore/:id" element={<AppDetail />} />
              <Route path="/burnerchat" element={<BurnerChat />} />
              <Route path="/cancel" element={<StripeCancel />} />
              <Route path="/success" element={<StripeSuccess />} />
              <Route path="/email-automation" element={<LexEmailAutomation />} />
              <Route path="/opencalendar" element={<LexOpenCalender />} />
              <Route path="/whentomeet" element={<LexWhenToMeet />} />
              <Route path="/message" element={<LexMessage />} />
              <Route path="/build" element={<AppBuilder />} />

              <Route path="/apps/super-admin" element={<SuperAdminPage />} />
              <Route path="/my-apps/advanced" element={<MyAppsAdvanced />} />
              <Route path="/my-apps" element={<MyAppRail />} />
              <Route path="/apps/advanced" element={<Navigate to="/my-apps/advanced" replace />} />
              <Route path="/apps" element={<Navigate to="/my-apps" replace />} />

              {/* Live routes (NEW + legacy) */}
              {/* ✅ NEW: /app/:ownerUid/:appId/live */}
              <Route path="/app/:ownerUid/:appId/live" element={<AppLive />} />
              <Route path="/app/:ownerUid/:appId/live/listing" element={<ListingPage />} />
              <Route path="/app/:ownerUid/:appId/live/listing-manage" element={<ManageListing />} />

              {/* ✅ LEGACY: /app/:appId/live */}
              <Route path="/app/:appId/live" element={<AppLive />} />
              <Route path="/app/:appId/live/listing" element={<ListingPage />} />
              <Route path="/app/:appId/live/listing-manage" element={<ManageListing />} />

              <Route path="/apps/:appId/listing/:templateId" element={<ListingPage />} />
              <Route path="/:slug/listing" element={<ListingPage />} />
              <Route path="/:slug/listing-manage" element={<ManageListing />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/pricing1" element={<Navigate to="/pricing" replace />} />

              {/* Short-link resolver: /i/{code} → redirects to /{slug}?ref=..&c=..
                  Must precede /:appSlug/:personSlug (two-segment) so /i/xxx
                  isn't swallowed by the profile route. */}
              <Route path="/i/:code" element={<ShortLinkResolver />} />

              {/* ✅ Friendly profile URL: /:appSlug/:personSlug
                  注意：必须放在 /:slug/listing /:slug/listing-manage 之后，
                  React Router v6 静态段优先，所以那两个会被先匹配。 */}
              <Route path="/:appSlug/:personSlug" element={<ProfileSlugLanding />} />

              <Route path="/:slug" element={<SlugLanding />} />
              <Route path="*" element={<NotFound />} />

              <Route path="/demo/sportscards" element={<DemoSportsCards />} />
              <Route path="/demo/dashboard" element={<SportsCardsDashboard />} />
              <Route path="/demo/cardshome" element={<CardsHome />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>

            {/* 覆盖路由（Modal 层）：当存在 background 时才渲染 */}
            {state?.background && (
              <ErrorBoundary>
              <Suspense fallback={<RouteFallback minHeight="40vh" />}>
              <Routes>
                {/* ✅ NEW live modal routes */}
                <Route path="/app/:ownerUid/:appId/live/listing" element={<ListingPage isModal />} />
                <Route
                  path="/app/:ownerUid/:appId/live/listing-manage"
                  element={<ManageListing isModal />}
                />

                {/* ✅ legacy live modal routes */}
                <Route path="/app/:appId/live/listing" element={<ListingPage isModal />} />
                <Route path="/app/:appId/live/listing-manage" element={<ManageListing isModal />} />

                <Route path="/apps/:appId/listing/:templateId" element={<ListingPage isModal />} />
                <Route path="/:slug/listing" element={<ListingPage isModal />} />
                <Route path="/:slug/listing-manage" element={<ManageListing isModal />} />
              </Routes>
              </Suspense>
              </ErrorBoundary>
            )}
          </div>
        </div>
      </div>

      {/* Site-wide bottom-LEFT promo pill (Lex AI pill is bottom-right).
          Self-gates: hidden for members / dismissed / on funnel routes. */}
      <ErrorBoundary>
        <PromoPill />
      </ErrorBoundary>
    </>
  );
}

/* ──────────────────────────────────────────────
   APP ROOT
   ────────────────────────────────────────────── */
function App() {
  return (
    // Outer ErrorBoundary catches even AuthProvider / Router init errors.
    // The inner one (around <Routes>) is what catches per-route failures.
    // Keeping both is intentional: if the outer fires, the user sees the
    // recovery card instead of the previous white screen-of-death.
    <ErrorBoundary>
    <AuthProvider>
      {/*
        future flags:
        - v7_startTransition: navigation state updates are wrapped in
          React.startTransition, so heavy lazy-route mounts don't block
          the previous page's responsiveness. This is exactly what
          eliminates the "tap → freeze briefly → new page" feel on
          mobile when switching routes.
        - v7_relativeSplatPath: ⚠️ DO NOT enable. It changes how
          <Link to="..."> resolves under splat routes like /profile/* .
          UserDashboard's Coins / Wallet / Account links use relative
          paths and rely on the v6 resolution; turning this on causes
          paths to keep appending (/profile/coins/coins/wallet/...).
          Silencing the v6→v7 console warning is not worth the
          regression. Leave at the v6 default (= flag absent).
      */}
      <Router future={{ v7_startTransition: true }}>
        <AppShell />
      </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
